/**
 * Budget per account — pure hulpfuncties (formatteren, groeperen, invoer lezen).
 * De rekenregels zelf staan in de engine (services/budget.py); hier alleen weergave.
 */

export const MAANDNAMEN = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

const EUR = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

/** € 12.345 — leeg bij null/undefined, zodat "niet ingevuld" iets anders is dan € 0. */
export function euro(waarde) {
  if (waarde === null || waarde === undefined || Number.isNaN(Number(waarde))) return '';
  return EUR.format(Math.round(Number(waarde)));
}

/** "12.500", "12500,50", "€ 1.000" → getal; leeg → null; onzin → NaN. */
export function leesBedrag(tekst) {
  if (tekst === null || tekst === undefined) return null;
  let s = String(tekst).replace(/[€\s]/g, '');
  if (!s) return null;
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '');
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

/** Bedrag voor een invoerveld (zonder €, zonder decimalen als die er niet zijn). */
export function alsInvoer(waarde) {
  if (waarde === null || waarde === undefined) return '';
  const n = Math.round(Number(waarde) * 100) / 100;
  return Number.isInteger(n) ? String(n) : String(n).replace('.', ',');
}

export const SOORTEN = [
  { soort: 'account', label: 'Accounts' },
  { soort: 'leeg', label: 'Accounts zonder omzet of offertes' },
  { soort: 'zonder', label: 'Zonder account' },
  { soort: 'groep', label: 'Excel-groepen' },
  { soort: 'wees', label: 'Budget overzetten (telt niet mee)' },
];

/** Regels filteren op zoekterm en groeperen per soort; accounts op omzet vorig jaar. */
export function groepeer(regels, zoek, vorigJaar) {
  const z = (zoek || '').trim().toLowerCase();
  const passend = (regels || []).filter((r) => !z || [r.naam, ...(r.bedrijfsnamen || [])]
    .some((n) => (n || '').toLowerCase().includes(z)));
  const sleutel = String(vorigJaar - 1);
  return SOORTEN.map(({ soort, label }) => {
    const lijst = passend.filter((r) => r.soort === soort);
    if (soort !== 'groep') {
      lijst.sort((a, b) => ((b.realisatie?.[sleutel] || 0) + (b.forecast_vorig || 0))
        - ((a.realisatie?.[sleutel] || 0) + (a.forecast_vorig || 0)) || (a.naam || '').localeCompare(b.naam || ''));
    }
    return { soort, label, regels: lijst };
  }).filter((g) => g.regels.length > 0);
}

/**
 * Verdeel een bedrag (euro's) naar gewichten, in centen met grootste-rest — dezelfde regel als
 * de engine (services/budget.verdeel), zodat de maanden exact optellen tot het target.
 */
export function verdeelBedrag(totaal, gewichten) {
  const g = (gewichten && gewichten.length ? gewichten : Array(12).fill(1)).map((x) => Math.max(Number(x) || 0, 0));
  const som = g.reduce((a, b) => a + b, 0);
  const w = som > 0 ? g : g.map(() => 1);
  const s = som > 0 ? som : w.length;
  const cent = Math.round(Math.abs(Number(totaal) || 0) * 100);
  const teken = Number(totaal) < 0 ? -1 : 1;
  const ruw = w.map((x) => (cent * x) / s);
  const basis = ruw.map((x) => Math.floor(x));
  let rest = cent - basis.reduce((a, b) => a + b, 0);
  const volgorde = ruw.map((x, i) => [x - basis[i], i]).sort((a, b) => b[0] - a[0] || a[1] - b[1]);
  for (let k = 0; k < volgorde.length && rest > 0; k += 1, rest -= 1) basis[volgorde[k][1]] += 1;
  return basis.map((c) => (teken * c) / 100);
}

/** Gat-weergave: positief = nog te vinden, ≤ 0 = gedekt, null = geen target. */
export function gatTekst(gat) {
  if (gat === null || gat === undefined) return { tekst: '', open: false };
  if (gat <= 0) return { tekst: 'gedekt', open: false };
  return { tekst: euro(gat), open: true };
}

/** Gewichten (fracties) ↔ procenten met één decimaal voor het invoerscherm. */
export const naarProcenten = (gewichten) => (gewichten || []).map((g) => Math.round(g * 1000) / 10);

export function uitProcenten(procenten) {
  const g = (procenten || []).map((p) => leesBedrag(p));
  if (g.length !== 12 || g.some((x) => x === null || Number.isNaN(x) || x < 0)) return null;
  const som = g.reduce((a, b) => a + b, 0);
  return som > 0 ? g.map((x) => x / som) : null;
}

/** Alleen de velden die echt veranderen (voorkomt lege opslagen en lege undo's). */
export function verschil(oud, nieuw) {
  const uit = {};
  Object.keys(nieuw).forEach((k) => {
    if (JSON.stringify(oud?.[k] ?? null) !== JSON.stringify(nieuw[k] ?? null)) uit[k] = nieuw[k];
  });
  return uit;
}
